import { Button, Callout, icons } from "@bruntworkwear-apps/ui";
import type { FeedbackReceipt } from "@/shared/api";
import styles from "../FeedbackDialog.module.css";

interface FiledProps {
  receipt: FeedbackReceipt;
  onClose: () => void;
  onAnother: () => void;
}

/**
 * What the reporter sees once the report is filed.
 *
 * The reference is the whole point of this panel. Sending something into a form
 * and getting a dialog that simply closes leaves no evidence it worked, and the
 * next thing that happens is the same report arriving again by email. A number
 * they can quote turns "did that go anywhere?" into a question with an answer.
 *
 * The issue URL is deliberately NOT shown. The accounting team has no access to
 * the repository, so a link would 404 at the exact moment they were feeling
 * most cooperative - it travels in the response for the log and for anyone
 * debugging, and stops there.
 *
 * On a local dry run the panel says outright that nothing was filed. A receipt
 * that looks real for a report that went nowhere is the one outcome worse than
 * an error, and this is the screen where that mistake would be made.
 */
export function FiledView({ receipt, onClose, onAnother }: FiledProps) {
  const dryRun = receipt.is_dry_run;

  return (
    <div className={styles.filed}>
      {dryRun ? (
        <icons.Info size={40} className={styles.filedIcon} aria-hidden />
      ) : (
        <icons.CheckCircle2 size={40} className={styles.filedIcon} aria-hidden />
      )}

      <h2 className={styles.filedTitle}>
        {dryRun ? "Nothing was filed." : "Thank you - that is with the team."}
      </h2>

      {dryRun ? (
        <Callout tone="warning">
          This is local development with no GitHub token, so the report was rendered and{" "}
          <strong>discarded</strong>. The issue it would have created is in the API log - set
          <code> FEEDBACK_GITHUB_TOKEN</code> to file for real.
        </Callout>
      ) : (
        <Callout tone="info">
          Your reference is <strong>{receipt.reference}</strong>. Quote it if you need to chase
          this up, or if you remember something else about it.
        </Callout>
      )}

      {!dryRun && (
        <p className={styles.filedNote}>
          Somebody will read it. If it turns out to be urgent and you have not heard back, tell
          the team directly rather than sending it again.
        </p>
      )}

      <div className={styles.actions}>
        <Button onClick={onClose}>Back to what I was doing</Button>
        <Button variant="ghost" onClick={onAnother}>
          {dryRun ? "Try another" : "Report something else"}
        </Button>
      </div>
    </div>
  );
}
